import * as StellarSdk from '@stellar/stellar-sdk';
import { xdr } from '@stellar/stellar-sdk';

import { BATCH_CONTRACT_METHOD } from 'constants/soroban';

import PromisedTimeout from 'helpers/promised-timeout';

import Connection from 'services/soroban/connection/connection';
import {
    contractIdToScVal,
    publicKeyToScVal,
    scValToArray,
} from 'services/soroban/utils/scValHelpers';

export type SimulateBatchOptions = {
    // Number of adjacent calls that must stay in the same batch
    // (e.g. estimate + fees pairs); chunking and budget-bisecting
    // never split a unit.
    unitSize?: number;
    // How many calls to pack into a single simulation. Heavy calls need a
    // small value; cheap reads (token metadata) can go much higher. Budget
    // overflows are bisected automatically, so this is a starting point,
    // not a hard limit.
    maxCallsPerBatch?: number;
};

export const buildBatchCall = (contractId: string, method: string, args: xdr.ScVal[]): xdr.ScVal =>
    scValToArray([contractIdToScVal(contractId), xdr.ScVal.scvSymbol(method), scValToArray(args)]);

// Global cap on concurrent RPC simulations across ALL batch groups (rewards,
// positions, balances, metadata, ...). Without it, several groups starting at
// once (e.g. right after login) stack up to dozens of parallel POSTs and can
// crowd out other network activity.
const GLOBAL_CONCURRENCY = 4;
let runningSimulations = 0;
const pendingSimulations: (() => void)[] = [];

const acquireSimulationSlot = (): Promise<void> =>
    new Promise(resolve => {
        if (runningSimulations < GLOBAL_CONCURRENCY) {
            runningSimulations += 1;
            resolve();
            return;
        }
        pendingSimulations.push(() => {
            runningSimulations += 1;
            resolve();
        });
    });

const releaseSimulationSlot = (): void => {
    runningSimulations -= 1;
    const next = pendingSimulations.shift();
    if (next) {
        next();
    }
};

const simulateBatchUnits = async (
    connection: Connection,
    batchContractId: string,
    accountId: string,
    units: xdr.ScVal[][],
    attempt = 0,
): Promise<xdr.ScVal[]> => {
    const MAX_TRANSPORT_RETRIES = 2;
    const retryable = attempt < MAX_TRANSPORT_RETRIES;

    let res: StellarSdk.rpc.Api.SimulateTransactionSuccessResponse;

    // The slot is held only for the network call itself (not for retries or
    // recursive bisecting), so a waiting chunk can never deadlock the pool.
    await acquireSimulationSlot();

    try {
        res = await connection
            .buildSmartContractTxForSimulate(
                accountId,
                batchContractId,
                BATCH_CONTRACT_METHOD.batch,
                scValToArray([publicKeyToScVal(accountId)]),
                scValToArray(units.flat()),
                // true = batcher returns the results of every call,
                // which is the whole point of a read batch
                xdr.ScVal.scvBool(true),
            )
            // silent while the failure is recoverable: over-budget chunks
            // are bisected and transport hiccups are retried, only the
            // final failure is reported
            .then(tx => connection.simulateTx(tx, { silent: units.length > 1 || retryable }));
    } catch (error) {
        releaseSimulationSlot();
        // Transport error (network hiccup, RPC rate limit / 503):
        // back off and retry the same chunk.
        if (retryable) {
            await PromisedTimeout(500 * (attempt + 1));
            return simulateBatchUnits(connection, batchContractId, accountId, units, attempt + 1);
        }
        throw error;
    }

    releaseSimulationSlot();

    if (res.result) {
        return res.result.retval.value() as unknown as xdr.ScVal[];
    }

    const simulationError = String(
        (res as unknown as StellarSdk.rpc.Api.SimulateTransactionErrorResponse).error ?? '',
    );

    // The simulation CPU budget is limited: if the batch doesn't fit,
    // bisect it (preserving unit boundaries) until chunks fit.
    if (simulationError.includes('ExceededLimit') && units.length > 1) {
        const mid = Math.ceil(units.length / 2);
        const left = await simulateBatchUnits(
            connection,
            batchContractId,
            accountId,
            units.slice(0, mid),
        );
        const right = await simulateBatchUnits(
            connection,
            batchContractId,
            accountId,
            units.slice(mid),
        );
        return [...left, ...right];
    }

    throw new Error('simulateBatch error');
};

/**
 * Simulates a list of read-only contract calls through the batcher contract,
 * chunking them to stay within per-simulation resource limits.
 * Returns one retval per call, in the same order.
 */
export const simulateBatch = async (
    connection: Connection,
    batchContractId: string,
    accountId: string,
    calls: xdr.ScVal[],
    { unitSize = 1, maxCallsPerBatch = 4 }: SimulateBatchOptions = {},
): Promise<xdr.ScVal[]> => {
    if (!calls.length) {
        return [];
    }

    const units: xdr.ScVal[][] = [];
    for (let i = 0; i < calls.length; i += unitSize) {
        units.push(calls.slice(i, i + unitSize));
    }

    const unitsPerChunk = Math.max(1, Math.floor(maxCallsPerBatch / unitSize));
    const chunks: xdr.ScVal[][][] = [];
    for (let i = 0; i < units.length; i += unitsPerChunk) {
        chunks.push(units.slice(i, i + unitsPerChunk));
    }

    // Limited concurrency: full parallel bursts get rate-limited (503) by
    // public RPC nodes, while strictly sequential execution makes large
    // batches painfully slow.
    const CONCURRENCY = 3;
    const results: xdr.ScVal[][] = new Array(chunks.length);
    let cursor = 0;

    const worker = async () => {
        while (cursor < chunks.length) {
            const index = cursor;
            cursor += 1;
            results[index] = await simulateBatchUnits(
                connection,
                batchContractId,
                accountId,
                chunks[index],
            );
        }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, () => worker()));

    return results.flat();
};
